/*
 * elova-hero-video.js
 *
 * Reduced-motion control for the homepage hero background video
 * (blocks/elova-hero-motion.liquid).
 *
 * The <video> ships with `autoplay muted loop playsinline` so it still plays
 * with JS disabled — muted + autoplay + playsinline together are what allow
 * autoplay on mobile at all. This component's job is the inverse: when the
 * user prefers reduced motion the video must genuinely NOT play (pausing it,
 * not merely hiding it). Because the poster IS the video's first frame, a
 * paused-and-rewound video is visually identical to the poster.
 *
 * Also re-applies when the OS motion preference changes mid-session.
 *
 * Loaded deferred from the hero block itself (not theme.liquid) so it never
 * ships on pages without the hero.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class ElovaHeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    this.applyMotionPreference = this.applyMotionPreference.bind(this);

    this.applyMotionPreference();
    this.motionQuery.addEventListener('change', this.applyMotionPreference);
  }

  disconnectedCallback() {
    if (this.motionQuery) {
      this.motionQuery.removeEventListener('change', this.applyMotionPreference);
    }
  }

  applyMotionPreference() {
    if (this.motionQuery.matches) {
      // Reduced motion: stop playback and rewind to the poster frame.
      this.video.removeAttribute('autoplay');
      this.video.pause();
      try {
        this.video.currentTime = 0;
      } catch (error) {
        // Seeking before metadata loads can throw; the poster covers it.
      }
      return;
    }

    // Motion allowed: play. Re-assert the muted *property* (not just the HTML
    // attribute) immediately before play() — iOS/WebKit checks the property to
    // decide whether autoplay is permitted; the attribute alone can be ignored,
    // leaving a dead native play button. A rejected promise (autoplay blocked by
    // the browser, e.g. data saver) leaves the poster showing — never a blank hero.
    this.video.muted = true;
    const played = this.video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {});
    }
  }
}

customElements.define('elova-hero-video', ElovaHeroVideo);
